import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";

import OnboardingPanelFooter from "features/courier-onboarding/components/OnboardingPanelFooter";
import PersonalFields from "features/courier-onboarding/components/fields/PersonalFields";
import PersonalDetailsFields from "features/courier-onboarding/components/fields/PersonalDetailsFields";
import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import {
  validateLeadBasicInfo,
  validatePersonalDetails,
  validatePersonalIdentityInfo,
} from "features/courier-onboarding/schemas";
import {
  checkRegistration,
  createCourierLead,
  updateCourierLeadProgress,
} from "features/courier-onboarding/services/courierOnboardingService";
import {
  type VehicleType,
} from "features/courier-onboarding/types";
import {
  DATA_STEP_FIELD_ORDER,
  firstInvalidFieldKey,
  scrollToInvalidField,
} from "features/courier-onboarding/utils/scrollToInvalidField";

/** Narrow an unknown thrown value to an HTTP status code, when present. */
const errorStatus = (err: unknown): number | undefined => {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { status?: number } }).response;
    return response?.status;
  }
  return undefined;
};

interface Props {
  onReadyForOtp: () => void;
  onBack?: () => void;
  lockedCity?: boolean;
  allowedVehicles?: VehicleType[];
}

/**
 * Single "data" step of the 3-step wizard: identity, contact, delivery area,
 * languages, vehicle and experience on one screen. Validates everything at
 * once, then creates the lead and opens the SMS code screen.
 */
const DataStep: React.FC<Props> = ({
  onReadyForOtp,
  onBack,
  lockedCity = false,
  allowedVehicles,
}) => {
  const { t } = useTranslation();
  const { data, setMetadata } = useCourierOnboarding();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;
    const first = firstInvalidFieldKey(errors, DATA_STEP_FIELD_ORDER);
    if (first) scrollToInvalidField(first);
  }, [errors]);

  const handleNext = async () => {
    const p = data.personalInfo;
    const errs: Record<string, string> = {
      ...validateLeadBasicInfo(p),
      ...validatePersonalIdentityInfo(p),
      ...validatePersonalDetails(p),
    };
    if (data.vehicleInfo.vehicleTypes.length === 0) {
      errs.vehicle = "onboarding.errors.vehicleRequired";
    }
    if (data.experienceInfo.hasDeliveryExperience === null) {
      errs.experience = "onboarding.errors.experienceRequired";
    } else if (
      data.experienceInfo.hasDeliveryExperience &&
      !data.experienceInfo.yearsOfExperienceRange
    ) {
      errs.years = "onboarding.errors.yearsRequired";
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setApiError(null);
    setDuplicate(false);

    // Early duplicate guard: block a phone number or ID that already completed
    // a registration before creating a new lead, and send them to login instead.
    try {
      const reg = await checkRegistration({
        phone: p.phoneNumber,
        taxId: p.taxId,
      });
      if (reg.exists) {
        setDuplicate(true);
        setSaving(false);
        return;
      }
    } catch {
      // Fail open: if the check is unavailable, continue and let the backend
      // enforce the duplicate rule on lead creation / submit.
    }

    try {
      let leadId = data.metadata.leadId;
      if (!leadId) {
        const lead = await createCourierLead({
          fullName: p.fullName,
          phoneNumber: p.phoneNumber,
          email: p.email,
          currentStep: "personal",
          nextStep: "personal",
          source: "CourierConnect",
          metadata: {
            documentsFirst: data.metadata.documentsFirst,
            businessTrack: data.metadata.businessTrack,
          },
        });
        leadId = lead.leadId;
        setMetadata({ leadId: lead.leadId, resumeToken: lead.resumeToken });
      }

      const { phoneVerificationToken: _token, ...leadMetadata } = data.metadata;
      await updateCourierLeadProgress(leadId, {
        lastCompletedStep: "personal",
        currentStep: "personal",
        nextStep: "personal",
        draftData: {
          personalInfo: data.personalInfo,
          vehicleInfo: data.vehicleInfo,
          experienceInfo: data.experienceInfo,
          metadata: leadMetadata,
        },
        metadata: leadMetadata,
      });
      onReadyForOtp();
    } catch (err) {
      if (errorStatus(err) === 409) {
        setDuplicate(true);
      } else {
        setApiError("onboarding.errors.saveLeadFailed");
      }
    } finally {
      setSaving(false);
    }
  };

  const clearDuplicate = () => {
    if (duplicate) setDuplicate(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <PersonalFields errors={errors} onContactChange={clearDuplicate} />
      <PersonalDetailsFields
        errors={errors}
        lockedCity={lockedCity}
        allowedVehicles={allowedVehicles}
      />

      {duplicate && (
        <div className="rounded-2xl border border-haat-red-200 bg-haat-red-50 p-4 flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-white text-haat-red-700 flex items-center justify-center flex-shrink-0">
            <ShieldAlert size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-haat-red-900">
              {t("onboarding.errors.alreadyRegisteredTitle")}
            </p>
            <p className="text-xs text-haat-red-800 mt-0.5">
              {t("onboarding.errors.alreadyRegistered")}
            </p>
            <Link
              to="/login"
              className="inline-flex mt-2 text-xs font-semibold text-haat-red-700 underline underline-offset-2"
            >
              {t("onboarding.errors.alreadyRegisteredCta")}
            </Link>
          </div>
        </div>
      )}
      {apiError && !duplicate ? (
        <span className="text-sm font-medium text-haat-red-700">
          {t(apiError)}
        </span>
      ) : null}

      <OnboardingPanelFooter>
        <div className="flex flex-col gap-2">
          {Object.keys(errors).length > 0 ? (
            <p className="text-xs font-medium text-haat-red-700">
              {t("onboarding.errors.fixHighlightedFields")}
            </p>
          ) : null}
          <div className="flex justify-between gap-3">
          {onBack ? (
            <button type="button" onClick={onBack} className="btn-outline">
              {t("common.back")}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={handleNext}
            className="btn-primary"
            disabled={saving || duplicate}
          >
            {saving
              ? (t("common.saving") ?? t("common.next"))
              : t("common.next")}
          </button>
        </div>
        </div>
      </OnboardingPanelFooter>
    </div>
  );
};

export default DataStep;
